import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { useAuth } from "./AuthContext";
import { api } from "../lib/api";
import { logger } from "../lib/logger";
import {
  ensureNotificationPermission,
  getTestNotificationDescription,
  playPing,
  showBrowserNotification,
  showIncomingToast,
} from "../lib/chatNotifications";

const WS_RECONNECT_MS = 3000;
const THREAD_POLL_MS = 30000;
const PRESENCE_POLL_MS = 25000;

const ChatContext = createContext(null);

function buildWsUrl() {
  const base = process.env.REACT_APP_BACKEND_URL;
  if (!base) return null;
  return base.replace(/^http/, "ws") + "/api/ws/chat";
}

function closeWs(ws, label = "ws") {
  try { ws?.close(); }
  catch (e) { logger.warn(`${label} close failed:`, e?.message); }
}

function safeWsSend(ws, payload, label = "ws-send") {
  if (!ws || ws.readyState !== WebSocket.OPEN) return false;
  try {
    ws.send(JSON.stringify(payload));
    return true;
  } catch (e) {
    logger.warn(`${label} failed:`, e?.message);
    return false;
  }
}

function notifySubscribers(subs, message, readAck) {
  subs.forEach((cb) => {
    try { cb(message, readAck); }
    catch (e) { logger.warn("chat subscriber threw:", e?.message); }
  });
}

export function ChatProvider({ children }) {
  const { user } = useAuth();
  const [connected, setConnected] = useState(false);
  const [threads, setThreads] = useState([]);
  const [activeOtherId, setActiveOtherId] = useState(null);
  // Live map of counsellor_id → status ('available' | 'in_session'). Anyone not in the map is 'away'.
  const [counsellorPresence, setCounsellorPresence] = useState({});
  // Live map of client_id → 'in_session'. Used on counsellor-facing views.
  const [clientPresence, setClientPresence] = useState({});
  // Pending session-ready alerts (client-only).
  const [pendingSessionReady, setPendingSessionReady] = useState([]);
  // Pending client-joined alerts (counsellor-only).
  const [pendingClientJoined, setPendingClientJoined] = useState([]);
  const subscribersRef = useRef(new Set());
  const wsRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const activeOtherIdRef = useRef(null);
  const userRef = useRef(null);

  useEffect(() => { activeOtherIdRef.current = activeOtherId; }, [activeOtherId]);
  useEffect(() => { userRef.current = user; }, [user]);

  const totalUnread = useMemo(
    () => threads.reduce((sum, t) => sum + (t.unread || 0), 0),
    [threads]
  );

  const loadThreads = useCallback(async () => {
    if (!userRef.current) return [];
    try {
      const { data } = await api.get("/messages/threads");
      setThreads(data);
      return data;
    } catch (e) {
      logger.warn("loadThreads failed:", e?.message);
      return [];
    }
  }, []);

  const loadPresence = useCallback(async () => {
    if (!userRef.current) return;
    try {
      const { data } = await api.get("/presence/status");
      const online = new Set(data?.online_ids || []);
      const busy = new Set(data?.in_session_counsellor_ids || []);
      const cMap = {};
      online.forEach((id) => { cMap[id] = "available"; });
      busy.forEach((id) => { cMap[id] = "in_session"; });
      setCounsellorPresence(cMap);
      const clientMap = {};
      (data?.in_session_client_ids || []).forEach((id) => { clientMap[id] = "in_session"; });
      setClientPresence(clientMap);
    } catch (e) {
      logger.warn("loadPresence failed:", e?.message);
    }
  }, []);

  const loadPendingSessionReady = useCallback(async () => {
    if (!userRef.current) return;
    const role = userRef.current.role;
    if (role === "client" || role === "parent") {
      try {
        const { data } = await api.get("/notifications/session-ready");
        setPendingSessionReady(Array.isArray(data) ? data : []);
      } catch (e) { logger.warn("loadPendingSessionReady failed:", e?.message); }
    }
    if (role === "counsellor") {
      try {
        const { data } = await api.get("/notifications/client-joined");
        setPendingClientJoined(Array.isArray(data) ? data : []);
      } catch (e) { logger.warn("loadPendingClientJoined failed:", e?.message); }
    }
  }, []);

  const ackSessionReady = useCallback(async (bookingId) => {
    if (!bookingId) return;
    setPendingSessionReady((list) => list.filter((n) => n.booking_id !== bookingId));
    try { await api.post(`/notifications/session-ready/${bookingId}/ack`); }
    catch (e) { logger.warn("ackSessionReady failed:", e?.message); }
  }, []);

  const ackClientJoined = useCallback(async (bookingId) => {
    if (!bookingId) return;
    setPendingClientJoined((list) => list.filter((n) => n.booking_id !== bookingId));
    try { await api.post(`/notifications/client-joined/${bookingId}/ack`); }
    catch (e) { logger.warn("ackClientJoined failed:", e?.message); }
  }, []);

  // Ask for browser-notification permission lazily on first click after login.
  useEffect(() => {
    if (!user || typeof Notification === "undefined") return;
    if (Notification.permission !== "default") return;
    const ask = () => {
      ensureNotificationPermission();
      window.removeEventListener("click", ask);
    };
    window.addEventListener("click", ask, { once: true });
  }, [user]);

  /** Handles a single incoming "message" frame — extracted for readability. */
  const handleIncomingMessage = useCallback((m, ws) => {
    const me = userRef.current?.user_id;
    const otherId = m.sender_id === me ? m.recipient_id : m.sender_id;
    const isIncoming = m.sender_id !== me;

    notifySubscribers(subscribersRef.current, m, null);
    loadThreads();

    if (!isIncoming) return;
    const isActiveConvo = otherId === activeOtherIdRef.current;
    if (isActiveConvo) {
      safeWsSend(ws, { type: "read", other_id: otherId }, "auto-ack-read");
      return;
    }
    playPing();
    showBrowserNotification({ name: m.sender_name || "New message", user_id: m.sender_id }, m.body);
    showIncomingToast(m);
  }, [loadThreads]);

  // Connect WebSocket once user is known
  const connect = useCallback(() => {
    const wsUrl = buildWsUrl();
    if (!wsUrl || !userRef.current) return;
    closeWs(wsRef.current, "stale-ws");
    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onopen = () => setConnected(true);
    ws.onclose = () => {
      setConnected(false);
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = setTimeout(connect, WS_RECONNECT_MS);
    };
    ws.onerror = () => { /* onclose will run */ };
    ws.onmessage = (ev) => {
      let data;
      try { data = JSON.parse(ev.data); }
      catch (e) { logger.warn("Bad WS frame:", e?.message); return; }

      if (data.type === "message" && data.message) {
        handleIncomingMessage(data.message, ws);
      } else if (data.type === "read") {
        notifySubscribers(subscribersRef.current, null, data);
      } else if (data.type === "session_ready" && data.booking_id) {
        // Counsellor pressed "Start session". Add to persistent list + push a toast + play a soft ping.
        setPendingSessionReady((list) => {
          if (list.some((n) => n.booking_id === data.booking_id)) return list;
          return [
            {
              booking_id: data.booking_id,
              counsellor_name: data.counsellor_name || "Your counsellor",
              started_at: data.started_at,
              scheduled_at: data.scheduled_at,
            },
            ...list,
          ];
        });
        try { playPing(); } catch (e) { logger.debug("ping sound failed (ignored):", e?.message); }
        toast.success(`${data.counsellor_name || "Your counsellor"} is ready — join now`, {
          duration: 10000,
          action: {
            label: "Join",
            onClick: () => { window.location.href = `/session/${data.booking_id}`; },
          },
        });
      } else if (data.type === "client_joined" && data.booking_id) {
        // Client just hit Join. Notify the counsellor (if this WS belongs to them).
        setPendingClientJoined((list) => {
          if (list.some((n) => n.booking_id === data.booking_id)) return list;
          return [
            {
              booking_id: data.booking_id,
              client_name: data.client_name || "Your client",
              joined_at: data.joined_at,
              scheduled_at: data.scheduled_at,
            },
            ...list,
          ];
        });
        try { playPing(); } catch (e) { logger.debug("ping sound failed (ignored):", e?.message); }
        toast.success(`${data.client_name || "Your client"} has joined the session`, {
          duration: 10000,
          action: {
            label: "Open",
            onClick: () => { window.location.href = `/session/${data.booking_id}`; },
          },
        });
      } else if (data.type === "client_waiting" && data.booking_id) {
        // Counsellor gets a soft heads-up when the client first opens the waiting room.
        // Fires only on the FIRST heartbeat (backend deduplicates) so it doesn't spam.
        try { playPing(); } catch (e) { logger.debug("ping sound failed (ignored):", e?.message); }
        toast.info(`${data.client_name || "Your client"} is waiting in the session lobby`, {
          duration: 8000,
          action: {
            label: "Start now",
            onClick: () => { window.location.href = `/session/${data.booking_id}`; },
          },
        });
      }
    };
  }, [handleIncomingMessage]);

  useEffect(() => {
    if (!user) {
      setThreads([]);
      setCounsellorPresence({});
      setClientPresence({});
      setPendingSessionReady([]);
      setPendingClientJoined([]);
      closeWs(wsRef.current);
      return undefined;
    }
    loadThreads();
    loadPresence();
    loadPendingSessionReady();
    connect();
    const pollId = setInterval(() => { loadThreads(); }, THREAD_POLL_MS);
    const presenceId = setInterval(() => { loadPresence(); }, PRESENCE_POLL_MS);
    return () => {
      clearInterval(pollId);
      clearInterval(presenceId);
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
      closeWs(wsRef.current);
    };
  }, [user, connect, loadThreads, loadPresence, loadPendingSessionReady]);

  const sendMessage = useCallback((recipient_id, body) =>
    safeWsSend(wsRef.current, { type: "send", recipient_id, body }, "send-msg"),
  []);

  const markRead = useCallback((other_id) => {
    if (!other_id) return;
    safeWsSend(wsRef.current, { type: "read", other_id }, "mark-read");
  }, []);

  const subscribe = useCallback((cb) => {
    subscribersRef.current.add(cb);
    return () => subscribersRef.current.delete(cb);
  }, []);

  const testNotification = useCallback(async () => {
    await ensureNotificationPermission();
    playPing();
    const popped = showBrowserNotification(
      { name: "Test ping", user_id: "test" },
      "If you can see this, desktop notifications are working.",
      { forceShow: true }
    );
    toast.message("Test notification", {
      description: getTestNotificationDescription(popped),
      duration: 5000,
    });
  }, []);

  const value = useMemo(
    () => ({
      connected,
      threads,
      totalUnread,
      activeOtherId,
      setActiveOtherId,
      sendMessage,
      markRead,
      subscribe,
      refreshThreads: loadThreads,
      testNotification,
      // Presence
      counsellorPresence,
      clientPresence,
      // Returns 'available' | 'in_session' | 'away'
      getCounsellorPresence: (id) => counsellorPresence[id] || "away",
      getClientPresence: (id) => clientPresence[id] || "away",
      // Legacy boolean helper — true when 'available' OR 'in_session'
      isCounsellorOnline: (id) => !!counsellorPresence[id],
      // Notifications
      pendingSessionReady,
      pendingClientJoined,
      ackSessionReady,
      ackClientJoined,
      refreshPresence: loadPresence,
      refreshPendingSessionReady: loadPendingSessionReady,
    }),
    [connected, threads, totalUnread, activeOtherId, sendMessage, markRead, subscribe, loadThreads, testNotification, counsellorPresence, clientPresence, pendingSessionReady, pendingClientJoined, ackSessionReady, ackClientJoined, loadPresence, loadPendingSessionReady]
  );

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
}

export const useChat = () => useContext(ChatContext) || {};
